import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { priceFormatter } from 'utils/Numbers';
import { buildProductStructuredData } from 'utils/StructuredData';
import Modal from 'react-modal';
import ModalB from 'components/Modal/ModalB';
import AffirmContainer from 'containers/AffirmContainer/AffirmContainer';
import BuildChildren from 'utils/BuildChildren';
import { isNumber, reduce, find, isEmpty, filter, sortBy, findIndex } from 'lodash';
import classes from './CardProductB.module.scss';

const renderPrice = (price, originalPrice, className = '') => (
  price >= 0 &&
    <div className={className}>
      <div className={classes.price}>{priceFormatter(price)}</div>
      {originalPrice > price &&
        <div className={classes.originalPrice}>{priceFormatter(originalPrice)}</div>}
    </div>
);

const renderButton = (button, className = '', hideLeftIcon) => (
  button &&
    <div className={className}>
      {BuildChildren(hideLeftIcon ? { ...button, leftIcon: '' } : button)}
    </div>
);

const renderSecondaryContentForDesktop = (price, originalPrice, button, className = '', hideLeftIcon) => (
  <div
    className={classNames(classes.secondaryContent, className)}
  >
    {renderPrice(price, originalPrice, classNames(classes.prices, className))}
    {renderButton(button, classNames(classes.button, className), hideLeftIcon)}
  </div>
);

const renderItem = (
  price,
  originalPrice,
  isAffirmEligible,
  displayName,
  subtitle,
  description,
  button,
  productId,
  showDetails,
  imageURI,
  brand,
  currency,
  organization,
  hideLeftIcon,
) => (
  <div className={classes.optContainer} key={`${productId}-${displayName}`}>
    <script
      type='application/ld+json'
      dangerouslySetInnerHTML={
        {
          __html: buildProductStructuredData({
            displayName,
            description,
            imageURI,
            productId,
            brand,
            price,
            currency,
            organization,
          }),
        }
      }
    />
    <div className={classNames(classes.optLabel)}>
      <div className={classes.contentWrapper}>
        <div className={classes.content}>
          <div className={classes.titleAndInfoWrapper}>
            {displayName &&
              <div
                className={classes.optTitle}
                dangerouslySetInnerHTML={{ __html: displayName }}
              />}
          </div>
          {subtitle &&
            <div
              className={classes.subtitle}
              dangerouslySetInnerHTML={{ __html: subtitle }}
            />}
          {renderPrice(price, originalPrice, classes.hideOnDesktop)}
          {price >= 0 &&
            <div className={classes.affirmMessage}>
              <AffirmContainer price={price} isAffirmEligible={isAffirmEligible} className={classes.affirmMessage} />
            </div>}
          {description &&
            <button
              className={classes.link}
              aria-label='View Details'
              onClick={showDetails}
              type='button'
            >
              View Details
            </button>}
          {renderButton(button, classes.hideOnDesktop, hideLeftIcon)}
          {typeof window === 'undefined' &&
            <div
              className={classes.hide}
              dangerouslySetInnerHTML={{ __html: description }}
            />}
        </div>
      </div>
      {renderSecondaryContentForDesktop(price, originalPrice, button, classes.hideOnMobile, hideLeftIcon)}
    </div>
  </div>
);

const CardProductB = (props) => {
  const { items = [], hideLeftIcon = false } = props;

  const packageTabCategoryList = sortBy(reduce(items, (acc, item) => {
    if (!isEmpty(item.packageTabProductCategory) && !find(acc, item.packageTabProductCategory)) {
      acc.push(item.packageTabProductCategory);
    }
    return acc;
  }, []), ['ordinal', 'title']);

  const nonCategorizedItems = filter(items, (it) => isEmpty(it.packageTabProductCategory));

  const [modalOpt, setModalOpt] = useState(undefined);
  const [isOpen, setIsOpen] = useState(false);

  const toggleModal = (e, modalIdx) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isOpen) {
      setModalOpt(modalIdx);
    }
    setIsOpen(!isOpen);
  };

  return (
    <div className={classes.wrapper}>
      <div className={classes.container}>
        <div className={classes.infoContainer}>
          {
            !isEmpty(nonCategorizedItems) && nonCategorizedItems.map((option) => renderItem(
              option.price,
              option.originalPrice,
              option.isAffirmEligible,
              option.displayName,
              option.subtitle,
              option.description,
              option.button,
              option.productId,
              (e) => toggleModal(e, findIndex(items, (i) => (
                i.productId === option.productId && i.displayName === option.displayName))),
              option.imageURI,
              option.brand,
              option.currency,
              option.organization,
              hideLeftIcon,
            ))
          }
          {
            !isEmpty(packageTabCategoryList) && packageTabCategoryList.map((category) => {
              const itemsPerCategory = filter(items, (it) => (
                it.packageTabProductCategory &&
                it.packageTabProductCategory.ordinal === category.ordinal &&
                it.packageTabProductCategory.title === category.title
              ));

              return (
                <React.Fragment key={`category-${category.title}`}>
                  <h5 className={classes.categoryTitle}>{category.title}</h5>
                  {itemsPerCategory && itemsPerCategory.map((option) => renderItem(
                    option.price,
                    option.originalPrice,
                    option.isAffirmEligible,
                    option.displayName,
                    option.subtitle,
                    option.description,
                    option.button,
                    option.productId,
                    (e) => toggleModal(e, findIndex(items, (i) => (
                      i.productId === option.productId && i.displayName === option.displayName))),
                    option.imageURI,
                    option.brand,
                    option.currency,
                    option.organization,
                    hideLeftIcon,
                  ))}
                </React.Fragment>
              );
            })
          }
          <Modal
            isOpen={isOpen}
            onRequestClose={(e) => toggleModal(e, undefined)}
            onAfterClose={() => {
              setModalOpt(undefined);
            }}
            shouldCloseOnOverlayClick={false}
            ariaHideApp={false}
            className={classes.modal}
            overlayClassName={classes.overlayModal}
            closeTimeoutMS={250}
          >
            <ModalB hideModal={(e) => toggleModal(e, undefined)}>
              { isNumber(modalOpt) && modalOpt >= 0 && items[modalOpt] &&
                <div
                  className={classes.description}
                  dangerouslySetInnerHTML={{ __html: items[modalOpt].description }}
                />}
            </ModalB>
          </Modal>
        </div>
      </div>
    </div>
  );
};

const ButtonT = PropTypes.shape({
  subType: PropTypes.string.isRequired,
  title: PropTypes.string,
  href: PropTypes.string,
  target: PropTypes.string,
  leftIcon: PropTypes.string,
  rightIcon: PropTypes.string,
  onClick: PropTypes.func,
});

const ProductT = PropTypes.shape({
  /**
   * BDS Product ID
   */
  productId: PropTypes.number,
  /**
   * Image URL
  */
  imageURI: PropTypes.string,
  /**
   * Image alternate text that displays on hover over of image
  */
  imageAltText: PropTypes.string,
  /**
   * Text displayed at the top of each product
  */
  displayName: PropTypes.string,
  /**
   * Text displayed directly below the displayName
  */
  subtitle: PropTypes.string,
  /**
   * Text displayed after clicking `View Details` in a modal window
  */
  description: PropTypes.string,
  /**
   * Product brand, used for structured data
  */
  brand: PropTypes.string,
  /**
   * Product organization, used for structured data
  */
  organization: PropTypes.string,
  /**
   * Product currency code, used for structured data<br>
   * Example: `USD`<br>
   * https://en.wikipedia.org/wiki/ISO_4217 to search available currency codes
  */
  currency: PropTypes.string,
  /**
   * Product price, used for structured data
  */
  price: PropTypes.number,
  /**
   * Shown only if value is greater than `price`
  */
  originalPrice: PropTypes.number,
  /**
   * Button Object<br>
   * Refer to documentation on `Button` component
  */
  button: ButtonT,
  /**
   * Product Category Object
  */
  packageTabProductCategory: PropTypes.shape({
    ordinal: PropTypes.number,
    title: PropTypes.string,
  }),
  /**
   * Can you use affirm on this product
  */
  isAffirmEligible: PropTypes.bool,
});

CardProductB.propTypes = {
  /**
   * List of Product Objects
  */
  items: PropTypes.arrayOf(ProductT).isRequired,
  /**
   * Boolean to tell whether we must hide the left icon for the CartButtons if any
  */
  hideLeftIcon: PropTypes.bool,
};

export default CardProductB;
